/* ==========================================================================
   showroom-layer.js
   ==========================================================================
   Shared brain for the "See it in person" layer. One file drives all three
   versions — A (card in the buy box), B (plan-a-visit drawer) and C (teaser
   + full-width band) — because all three answer the same question and must
   never answer it differently on the same page.

   Vanilla JS, no libraries, loaded with defer.

   WHAT IT DOES NOT DO
   -------------------
   It does not build showroom rows. Every row, card and drawer pick is
   server-rendered by showroom-layer.liquid; this only rewrites the status
   pill, the sub-line and the opening-hours line, and re-orders rows with
   CSS `order`. That keeps the markup complete before JS runs and means a
   status update causes no layout shift.

   A HOLD IS A REQUEST
   -------------------
   Nothing here touches inventory, the cart or a draft order. Every
   confirmation says "we'll text you to confirm".
   ========================================================================== */

(function () {
  'use strict';

  var STATUS_ORDER = { this: 0, other: 1, samples: 2, none: 3 };
  var DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var STORE_TZ = 'America/Los_Angeles';

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /** sessionStorage is wrapped everywhere: it throws in some privacy modes. */
  function remember(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* no-op */ }
  }
  function recall(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  /**
   * The store's own clock, not the shopper's.
   *
   * A shopper in New York must still be told when the Olympia showroom
   * closes in Pacific time, so the day and the time both come from
   * Intl with an explicit timeZone rather than from the local Date.
   */
  function storeNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TZ,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });

    return {
      dayKey: String(out.weekday || '').slice(0, 3).toLowerCase(),
      minutes: (parseInt(out.hour, 10) || 0) * 60 + (parseInt(out.minute, 10) || 0)
    };
  }

  function toMinutes(hhmm) {
    var bits = String(hhmm || '').split(':');
    return (parseInt(bits[0], 10) || 0) * 60 + (parseInt(bits[1], 10) || 0);
  }

  function prettyTime(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + ' ' + suffix;
  }

  /** Parses the hours JSON once and caches it on the room. */
  function roomHours(room) {
    if (room._hours !== undefined) return room._hours;
    try {
      room._hours = typeof room.hours === 'string' ? JSON.parse(room.hours) : (room.hours || null);
    } catch (e) {
      room._hours = null;
    }
    return room._hours;
  }

  /**
   * "Open today until 6 PM" / "Opens today at 10 AM" / "Closed today".
   * Returns an empty string rather than a guess when there are no hours.
   */
  function openLine(room) {
    var hours = roomHours(room);
    if (!hours) return '';

    var now = storeNow();
    var today = hours[now.dayKey];

    if (!today || today === 'closed') {
      // Find the next day that is open so the line stays useful.
      var startIndex = DAY_KEYS.indexOf(now.dayKey);
      for (var i = 1; i <= 7; i++) {
        var key = DAY_KEYS[(startIndex + i) % 7];
        var next = hours[key];
        if (next && next !== 'closed') {
          var label = key.charAt(0).toUpperCase() + key.slice(1);
          return 'Closed today · Opens ' + label + ' ' + prettyTime(toMinutes(next.split('-')[0]));
        }
      }
      return 'Closed today';
    }

    var range = today.split('-');
    var opens = toMinutes(range[0]);
    var closes = toMinutes(range[1]);

    if (now.minutes < opens) return 'Opens today at ' + prettyTime(opens);
    if (now.minutes < closes) return 'Open today until ' + prettyTime(closes);
    return 'Closed for today';
  }

  /** Great-circle distance in miles, for the optional location sort. */
  function distanceMiles(lat1, lon1, lat2, lon2) {
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLon = (lon2 - lon1) * toRad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* ------------------------------------------------------------------ *
   * The layer
   * ------------------------------------------------------------------ */

  function ShowroomLayer() {
    var dataEl = $('[data-showroom-data]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[showroom] could not parse showroom data', e);
      return;
    }

    this.rooms = this.data.showrooms || [];
    if (!this.rooms.length) return;

    this.byHandle = {};
    var self = this;
    this.rooms.forEach(function (r) { self.byHandle[r.handle] = r; });

    this.demo = this.parseDemo(this.data.demoStatuses);
    this.coords = this.recallCoords();

    this.variant = this.readInitialVariant();

    this.bind();
    this.render();
    this.watchInventoryApp();
    this.restoreAfterPost();
  }

  /**
   * "olympia:this, bellevue:other" -> { olympia: 'this', bellevue: 'other' }
   *
   * A preview aid only: it is ignored the moment real floor_display
   * metaobjects exist, so turning on the real data cannot be silently
   * overridden by a setting somebody forgot to clear.
   */
  ShowroomLayer.prototype.parseDemo = function (raw) {
    var map = {};
    if (!raw || this.data.floorDisplayCount > 0) return map;

    String(raw).split(',').forEach(function (pair) {
      var bits = pair.split(':');
      if (bits.length !== 2) return;
      var handle = bits[0].trim().toLowerCase();
      var status = bits[1].trim().toLowerCase();
      if (handle && STATUS_ORDER[status] !== undefined) map[handle] = status;
    });
    return map;
  };

  /* ---- Variant ---- */

  ShowroomLayer.prototype.readInitialVariant = function () {
    var input = $('[data-pcfg-variant-id]');
    var id = input ? parseInt(input.value, 10) : null;

    // The configurator exposes its instance on the section root.
    var root = $('[data-section-type="product-configurator"]');
    var current = root && root.pcfg ? root.pcfg.currentVariant : null;

    if (current) return current;
    return id ? { id: id, options: [], sku: '' } : null;
  };

  ShowroomLayer.prototype.variantId = function () {
    return this.variant ? this.variant.id : null;
  };

  ShowroomLayer.prototype.variantTitle = function () {
    if (!this.variant) return '';
    var opts = (this.variant.options || []).filter(Boolean);
    return opts.length ? opts.join(' / ') : (this.variant.title || '');
  };

  /* ---- Live inventory, read from the multi-location app ---- */

  /**
   * The store's inventory feed already answers the question this card
   * asks, per variant, refreshed twice a day — it just answers it in a
   * third-party app's DOM rather than in Liquid.
   *
   *   Warehouse         In Stock & Ready for Delivery   .iia-in-stock
   *   Tacoma Showroom   On Display                      .iia-on-display
   *
   * The app renders client side about 200ms after load and re-renders on
   * every variant change, so this is read on every render and again
   * whenever the app mutates its own list.
   *
   * Returns null when the app has not rendered yet, which is meaningfully
   * different from "it rendered and this showroom is not in the list" —
   * the first falls through to the other sources, the second is a real
   * "not on the floor here".
   */
  ShowroomLayer.prototype.readInventoryApp = function () {
    var items = document.querySelectorAll('.iia-list-item');
    if (!items.length) return null;

    var self = this;
    var map = {};

    Array.prototype.forEach.call(items, function (item) {
      var titleEl = item.querySelector('.iia-title-text');
      if (!titleEl) return;

      // "Tacoma Showroom - On Display" -> "Tacoma Showroom"
      var name = String(titleEl.textContent || '').split(' - ')[0].trim();
      var handle = self.matchRoom(name);

      // Warehouse is Fife, not a showroom. It belongs in the delivery
      // line, and putting it in this card would invite a wasted trip.
      if (!handle) return;

      var statusEl = item.querySelector('.iia-extra-text');
      var cls = statusEl ? String(statusEl.className || '') : '';

      // Both states mean the piece is physically at that showroom in the
      // build currently selected — the app is per variant.
      if (/on-display|in-stock/i.test(cls)) map[handle] = 'this';
    });

    return map;
  };

  /** "Tacoma Showroom" -> the tacoma room, by handle or short name. */
  ShowroomLayer.prototype.matchRoom = function (name) {
    var n = String(name || '').toLowerCase().replace(/\bshowroom\b/g, '').trim();
    if (!n) return null;

    for (var i = 0; i < this.rooms.length; i++) {
      var room = this.rooms[i];
      if (room.handle === n) return room.handle;
      if (String(room.shortName || '').toLowerCase() === n) return room.handle;
      if (String(room.name || '').toLowerCase() === String(name).toLowerCase()) return room.handle;
    }
    return null;
  };

  /**
   * Re-render when the app rewrites its list. It does that on every
   * variant change, and its first render lands after ours.
   */
  ShowroomLayer.prototype.watchInventoryApp = function () {
    var self = this;
    var host = document.querySelector('#inventory-info-app') ||
      document.querySelector('.iia-container');

    if (!host || typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function () {
      window.clearTimeout(self._invTimer);
      // The app mutates in bursts; one render after it settles is enough.
      self._invTimer = window.setTimeout(function () { self.render(); }, 120);
    });

    observer.observe(host, { childList: true, subtree: true });
  };

  /* ---- Status ---- */

  /**
   * Status for one showroom against the current build:
   *   this    a floor model here matches the selected variant
   *   other   this model is here, in a different build
   *   samples not on the floor, but samples are carried
   */
ShowroomLayer.prototype.statusFor = function (handle) {
    var id = this.variantId();
    var displays = (this.data.floorDisplays || []).filter(function (fd) {
      return fd.showroom === handle;
    });

    /* Source order, most authoritative first:
         1. floor_display metaobjects — hand-curated, knows WHICH build
         2. the live inventory app    — per variant, refreshed twice daily
         3. demo_statuses             — a preview aid, nothing more
       The demo override is deliberately last so that turning on real data
       cannot be silently overridden by a setting somebody forgot to clear. */
    if (!displays.length && this.inventory) {
      if (this.inventory[handle]) {
        return { status: this.inventory[handle], label: '' };
      }
      // The app rendered and did not list this showroom. That is a real
      // answer, not missing data.
      var samplesList = this.data.samplesAt || [];
      if (!samplesList.length || samplesList.indexOf(handle) !== -1) {
        return { status: 'samples', label: '' };
      }
      return { status: 'none', label: '' };
    }

    if (!displays.length && this.demo[handle]) {
      return { status: this.demo[handle], label: '' };
    }

    for (var i = 0; i < displays.length; i++) {
      if (displays[i].variantIds.indexOf(id) !== -1) {
        return { status: 'this', label: displays[i].label || this.variantTitle() };
      }
    }

    if (displays.length) {
      var d = displays[0];
      return { status: 'other', label: d.label || d.variantTitles[0] || '' };
    }

    var samplesAt = this.data.samplesAt || [];
    // An empty list means every showroom carries samples.
    if (!samplesAt.length || samplesAt.indexOf(handle) !== -1) {
      return { status: 'samples', label: '' };
    }

    return { status: 'none', label: '' };
  };

  ShowroomLayer.prototype.pillText = function (status) {
    var word = this.data.optionWord || 'finish';
    if (status === 'this') return 'On display · this ' + word;
    if (status === 'other') return 'On display · other ' + word;
    if (status === 'samples') return word.charAt(0).toUpperCase() + word.slice(1) + ' samples';
    return 'Available to order';
  };

  ShowroomLayer.prototype.subText = function (result) {
    var product = this.data.productTitle || 'this piece';

    if (result.status === 'this') {
      return 'Sit in the exact build you have chosen.';
    }
    if (result.status === 'other') {
      return result.label ? 'On the floor in ' + result.label : 'On the floor in another build';
    }
    if (result.status === 'samples') {
      // Not 'every X and finish' — on a product whose option 2 IS the
      // finish that reads as 'every finish and finish'.
      return 'Samples of every ' + (this.data.optionWord || 'finish') + ' in store.';
    }
    return 'We can order ' + product + ' for you.';
  };

  /* ---- Rendering ---- */

  ShowroomLayer.prototype.render = function () {
    var self = this;
    var onFloor = [];

    // Re-read every time: the app rewrites its list on each variant change.
    this.inventory = this.readInventoryApp();

    $$('[data-shl-room], [data-shl-pick]').forEach(function (el) {
      var handle = el.getAttribute('data-shl-room');
      var room = self.byHandle[handle];
      if (!room) return;

      var result = self.statusFor(handle);

      if (result.status === 'this' || result.status === 'other') {
        if (onFloor.indexOf(room.shortName) === -1) onFloor.push(room.shortName);
      }

      var pill = $('[data-shl-pill]', el);
      if (pill) {
        pill.textContent = self.pillText(result.status);
        pill.className = 'shl__pill shl__pill--' + result.status +
          (pill.classList.contains('shl__pill--overlay') ? ' shl__pill--overlay' : '');
      }

      var sub = $('[data-shl-sub]', el);
      if (sub) sub.textContent = self.subText(result);

      var hours = $('[data-shl-hours]', el);
      if (hours) hours.textContent = openLine(room);

      // "none" rooms are hidden rather than shown as dead ends.
      el.hidden = result.status === 'none';

      // Sorting happens in CSS so nothing is moved in the DOM.
      var order = STATUS_ORDER[result.status] * 100 + (room.sortOrder || 0);
      if (self.coords) {
        room.distance = distanceMiles(self.coords.lat, self.coords.lng, room.lat, room.lng);
        order = STATUS_ORDER[result.status] * 100 + Math.round(room.distance);
        var dist = $('[data-shl-distance]', el);
        if (dist) dist.textContent = Math.round(room.distance) + ' mi';
      }
      el.style.order = String(order);

      // The booking link carries the build so the designer sees it.
      var book = $('[data-shl-book]', el);
      if (book) {
        var base = book.getAttribute('data-base-href') || '';
        if (base) {
          var joiner = base.indexOf('?') === -1 ? '?' : '&';
          book.href = base + joiner + 'product=' + encodeURIComponent(self.data.productTitle || '') +
            '&variant=' + encodeURIComponent(self.variantId() || '');
        }
      }

      // Samples rooms ask for samples, not for a hold.
      var holdLabel = $('[data-shl-hold-label]', el);
      if (holdLabel && result.status === 'samples') {
        holdLabel.textContent = 'Request samples at visit';
      }

      self.syncForm(el, result.status);
    });

    this.renderTeaser(onFloor);
    this.renderVisitSub(onFloor);
    this.renderDrawerBuild();
    this.announce(onFloor);
  };

  /** Keeps each hold form's hidden fields matching the current build. */
  ShowroomLayer.prototype.syncForm = function (scope, status) {
    var self = this;
    var id = this.variantId();
    var title = this.variantTitle();

    $$('[data-shl-variant-id]', scope).forEach(function (el) { el.value = id || ''; });
    $$('[data-shl-variant-title]', scope).forEach(function (el) { el.value = title; });
    $$('[data-shl-sku]', scope).forEach(function (el) {
      el.value = (self.variant && self.variant.sku) || '';
    });
    $$('[data-shl-request-type]', scope).forEach(function (el) {
      el.value = status === 'samples' ? 'samples' : 'hold';
    });

    // The "when" question is meaningless for a samples request.
    var when = $('.shl__when', scope);
    if (when) when.hidden = status === 'samples';
  };

  ShowroomLayer.prototype.renderTeaser = function (onFloor) {
    var teaser = $('[data-shl-teaser]');
    if (!teaser) return;

    // Version A already answers this, in more detail.
    if ($('[data-shl-card]')) {
      teaser.hidden = true;
      return;
    }

    var text = $('[data-shl-teaser-text]', teaser);
    if (text) {
      text.textContent = onFloor.length
        ? 'On display in ' + listWords(onFloor)
        : (this.data.optionWord || 'Finish') + ' and finish samples at all showrooms';
    }
    teaser.hidden = false;
  };

  /**
   * The visit block's sub-line, and optionally the block itself.
   *
   * With data-requires-floor set, the whole trigger disappears on a
   * product no showroom has on the floor — inviting someone to drive in
   * and sit in something that is not there is worse than not offering.
   * Re-tested on every build change, so switching to a leather that IS on
   * a floor brings the button back.
   */
  ShowroomLayer.prototype.renderVisitSub = function (onFloor) {
    var visit = $('[data-shl-visit]');

    if (visit && visit.getAttribute('data-requires-floor') === 'true') {
      visit.hidden = onFloor.length === 0;
    }

    var sub = $('[data-shl-visit-sub]');
    if (!sub) return;

    if (!onFloor.length) { sub.hidden = true; return; }
    sub.textContent = 'On display in ' + listWords(onFloor);
    sub.hidden = false;
  };

  ShowroomLayer.prototype.renderDrawerBuild = function () {
    var el = $('[data-shl-drawer-build]');
    if (!el) return;
    var title = this.variantTitle();
    el.textContent = (this.data.productTitle || '') + (title ? ' · ' + title : '');
  };

  function listWords(items) {
    if (items.length === 1) return items[0];
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  /** One debounced announcement for the whole layer. */
  ShowroomLayer.prototype.announce = function (onFloor) {
    var live = $('[data-shl-live]');
    if (!live) return;

    window.clearTimeout(this._liveTimer);
    var msg = onFloor.length
      ? 'On display in ' + listWords(onFloor)
      : 'Samples available at all showrooms';

    this._liveTimer = window.setTimeout(function () { live.textContent = msg; }, 700);
  };

  /* ------------------------------------------------------------------ *
   * Interactions
   * ------------------------------------------------------------------ */

  ShowroomLayer.prototype.bind = function () {
    var self = this;

    // The configurator already publishes this on every build change.
    document.addEventListener('variant:change', function (evt) {
      if (evt.detail && evt.detail.variant) {
        self.variant = evt.detail.variant;
        self.render();
      }
    });

    document.addEventListener('click', function (evt) {
      var t = evt.target;

      var toggle = t.closest && t.closest('[data-shl-room-toggle]');
      if (toggle) { self.toggleRoom(toggle); return; }

      var holdOpen = t.closest && t.closest('[data-shl-hold-open]');
      if (holdOpen) { self.openForm(holdOpen); return; }

      var cancel = t.closest && t.closest('[data-shl-form-cancel]');
      if (cancel) { self.closeForm(cancel); return; }

      var undo = t.closest && t.closest('[data-shl-undo]');
      if (undo) { self.resetRoom(undo); return; }

      var locate = t.closest && t.closest('[data-shl-locate]');
      if (locate) { self.locate(); return; }
    });

    // Remember which room submitted, so the posted-back page can re-open it.
    document.addEventListener('submit', function (evt) {
      var form = evt.target.closest && evt.target.closest('[data-shl-form], [data-shl-visit-form]');
      if (!form) return;
      self.writeBody(form);
      remember('shl:posted', form.getAttribute('data-room') || 'visit');
    });

    if ($('[data-shl-drawer]')) this.bindDrawer();
  };

  /** A readable summary in the email body, built from the hidden fields. */
  ShowroomLayer.prototype.writeBody = function (form) {
    var body = $('[data-shl-body]', form);
    if (!body) return;

    function val(sel) {
      var el = $(sel, form);
      return el ? el.value : '';
    }

    var day = val('[data-shl-field-day]') || (form.querySelector('[name="contact[requested_day]"]:checked') || {}).value || '';

    body.value = [
      'Showroom request',
      'Type: ' + val('[data-shl-request-type]'),
      'Showroom: ' + (val('[data-shl-field-showroom]') || (form.querySelector('[name="contact[showroom]"]') || {}).value || ''),
      'Product: ' + (this.data.productTitle || ''),
      'Build: ' + this.variantTitle(),
      'SKU: ' + val('[data-shl-sku]'),
      'Requested day: ' + day,
      'Requested time: ' + val('[data-shl-field-time]'),
      'Page: ' + val('[data-shl-page-url]')
    ].join('\n');
  };

  ShowroomLayer.prototype.toggleRoom = function (toggle) {
    var room = toggle.closest('[data-shl-room]');
    var open = toggle.getAttribute('aria-expanded') === 'true';

    // One row open at a time keeps the card short.
    $$('[data-shl-room-toggle]').forEach(function (other) {
      if (other === toggle) return;
      other.setAttribute('aria-expanded', 'false');
      var body = $('[data-shl-room-body]', other.closest('[data-shl-room]'));
      if (body) body.hidden = true;
      other.closest('[data-shl-room]').classList.remove('is-open');
    });

    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    room.classList.toggle('is-open', !open);
    var body = $('[data-shl-room-body]', room);
    if (body) body.hidden = open;
  };

  ShowroomLayer.prototype.openForm = function (btn) {
    var room = btn.closest('[data-shl-room]');
    $('[data-shl-actions]', room).hidden = true;
    $('[data-shl-form-wrap]', room).hidden = false;

    var first = $('input[type="text"], input[type="tel"]', room);
    if (first) first.focus();
  };

  ShowroomLayer.prototype.closeForm = function (btn) {
    var room = btn.closest('[data-shl-room]');
    $('[data-shl-form-wrap]', room).hidden = true;
    $('[data-shl-actions]', room).hidden = false;
  };

  ShowroomLayer.prototype.resetRoom = function (btn) {
    var room = btn.closest('[data-shl-room]');
    $('[data-shl-done]', room).hidden = true;
    $('[data-shl-actions]', room).hidden = false;
    remember('shl:posted', '');
  };

  /**
   * Shopify's contact form posts and reloads. On the way back the page
   * carries ?contact_posted=true, and sessionStorage remembers which room
   * sent it, so the right confirmation re-opens instead of the shopper
   * landing on a page that looks like nothing happened.
   */
  ShowroomLayer.prototype.restoreAfterPost = function () {
    if (window.location.search.indexOf('contact_posted=true') === -1) return;

    var handle = recall('shl:posted');
    if (!handle) return;

    if (handle === 'visit') {
      var drawer = $('[data-shl-drawer]');
      if (drawer) { this.openDrawer(); this.showPanel('done'); }
      return;
    }

    var room = $('[data-shl-room="' + handle + '"]');
    if (!room) return;

    var toggle = $('[data-shl-room-toggle]', room);
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
      room.classList.add('is-open');
    }
    var body = $('[data-shl-room-body]', room);
    if (body) body.hidden = false;

    $('[data-shl-form-wrap]', room).hidden = true;
    $('[data-shl-actions]', room).hidden = true;
    $('[data-shl-done]', room).hidden = false;

    room.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /* ---- Geolocation ---- */

  ShowroomLayer.prototype.recallCoords = function () {
    var raw = recall('shl:coords');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  };

  ShowroomLayer.prototype.locate = function () {
    var self = this;
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(function (pos) {
      self.coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      remember('shl:coords', JSON.stringify(self.coords));
      self.render();
    }, function () {
      // Denied: keep the default order and say nothing. Nagging a shopper
      // who just said no is how you never get permission again.
    }, { timeout: 8000, maximumAge: 600000 });
  };

  /* ------------------------------------------------------------------ *
   * Version B — the drawer
   * ------------------------------------------------------------------ */

  ShowroomLayer.prototype.bindDrawer = function () {
    var self = this;
    this.drawer = $('[data-shl-drawer]');
    this.backdrop = $('[data-shl-backdrop]');
    this.state = { room: null, mode: 'hold', day: null, time: null, step: 1 };

    $$('[data-shl-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.trigger = btn; self.openDrawer(); });
    });

    $$('[data-shl-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.closeDrawer(); });
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', function () { self.closeDrawer(); });
    }

    // Esc is handled on the drawer, not globally, so it cannot swallow
    // the key for anything else on the page.
    this.drawer.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') { evt.stopPropagation(); self.closeDrawer(); }
      if (evt.key === 'Tab') self.trapFocus(evt);
    });

    $$('[data-shl-pick]', this.drawer).forEach(function (card) {
      card.addEventListener('click', function () {
        self.state.room = card.getAttribute('data-shl-room');
        $$('[data-shl-pick]', self.drawer).forEach(function (c) {
          c.setAttribute('aria-pressed', String(c === card));
        });
        self.buildDays();
      });
    });

    $$('[data-shl-mode]', this.drawer).forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.mode = btn.getAttribute('data-shl-mode');
        $$('[data-shl-mode]', self.drawer).forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        self.buildTimes();
      });
    });

    var next = $('[data-shl-next]', this.drawer);
    var back = $('[data-shl-back]', this.drawer);
    if (next) next.addEventListener('click', function () { self.advance(1); });
    if (back) back.addEventListener('click', function () { self.advance(-1); });

    var ics = $('[data-shl-ics]', this.drawer);
    if (ics) ics.addEventListener('click', function () { self.downloadIcs(); });
  };

  ShowroomLayer.prototype.openDrawer = function () {
    this.renderDrawerBuild();
    this.backdrop.hidden = false;
    this.drawer.hidden = false;
    document.body.classList.add('shl-drawer-open');

    var focusable = this.focusable()[0];
    if (focusable) focusable.focus();
  };

  ShowroomLayer.prototype.closeDrawer = function () {
    this.drawer.hidden = true;
    this.backdrop.hidden = true;
    document.body.classList.remove('shl-drawer-open');
    if (this.trigger) this.trigger.focus();
  };

  ShowroomLayer.prototype.focusable = function () {
    return $$('button, [href], input, select, textarea', this.drawer)
      .filter(function (el) { return !el.disabled && el.offsetParent !== null; });
  };

  ShowroomLayer.prototype.trapFocus = function (evt) {
    var items = this.focusable();
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  };

  ShowroomLayer.prototype.showPanel = function (step) {
    this.state.step = step;
    $$('[data-shl-panel]', this.drawer).forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-shl-panel') !== String(step);
    });
    $$('.shl__step', this.drawer).forEach(function (el) {
      var n = parseInt(el.getAttribute('data-step'), 10);
      el.classList.toggle('is-done', typeof step === 'number' && n < step);
      el.classList.toggle('is-current', String(n) === String(step));
    });

    var back = $('[data-shl-back]', this.drawer);
    var next = $('[data-shl-next]', this.drawer);
    var foot = $('[data-shl-drawer-foot]', this.drawer);

    if (step === 'done') { foot.hidden = true; return; }
    foot.hidden = false;
    back.hidden = step === 1;
    next.hidden = step === 3;
    if (step === 3) this.fillSummary();
  };

  ShowroomLayer.prototype.advance = function (direction) {
    var step = this.state.step;

    if (direction === 1) {
      if (step === 1 && !this.state.room) return;
      if (step === 2 && (!this.state.day || !this.state.time)) return;
    }

    var nextStep = Math.min(3, Math.max(1, (typeof step === 'number' ? step : 1) + direction));
    this.showPanel(nextStep);
  };

  /** The next open days for the chosen showroom, skipping closed ones. */
  ShowroomLayer.prototype.buildDays = function () {
    var self = this;
    var wrap = $('[data-shl-days]', this.drawer);
    if (!wrap) return;

    var room = this.byHandle[this.state.room];
    var hours = roomHours(room) || {};
    var html = '';
    var made = 0;

    for (var offset = 0; offset < 14 && made < 5; offset++) {
      var d = new Date();
      d.setDate(d.getDate() + offset);
      var key = DAY_KEYS[d.getDay()];
      if (!hours[key] || hours[key] === 'closed') continue;

      var label = d.toLocaleDateString('en-US', { weekday: 'short' });
      var date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      html += '<button type="button" class="shl__day" data-shl-day="' + d.toISOString().slice(0, 10) +
        '" data-hours="' + hours[key] + '" aria-pressed="false">' +
        '<span class="shl__day-name">' + label + '</span>' +
        '<span class="shl__day-date">' + date + '</span></button>';
      made++;
    }

    wrap.innerHTML = html;

    // Render the time options straight away. Waiting for a day to be
    // picked left step 2 showing a 'Time' heading above nothing, which
    // reads as broken rather than as 'pick a day first'.
    this.buildTimes();

    $$('[data-shl-day]', wrap).forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.day = btn.getAttribute('data-shl-day');
        self.state.dayHours = btn.getAttribute('data-hours');
        $$('[data-shl-day]', wrap).forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        self.buildTimes();
      });
    });
  };

  /**
   * Appointment slots when a designer is involved, broad parts of the day
   * when it is just a hold. Both are REQUESTED times — the label says so.
   */
  ShowroomLayer.prototype.buildTimes = function () {
    var self = this;
    var wrap = $('[data-shl-times]', this.drawer);
    if (!wrap) return;

    var html = '';

    if (this.state.mode === 'hold_designer' && this.state.dayHours) {
      var range = this.state.dayHours.split('-');
      var start = toMinutes(range[0]);
      var end = toMinutes(range[1]);
      var interval = 90;

      for (var t = start; t + interval <= end; t += interval) {
        html += '<button type="button" class="shl__time" data-shl-time="' + prettyTime(t) +
          '" aria-pressed="false">' + prettyTime(t) + '</button>';
      }
    } else {
      ['Morning', 'Midday', 'Afternoon'].forEach(function (part) {
        html += '<button type="button" class="shl__time" data-shl-time="' + part +
          '" aria-pressed="false">' + part + '</button>';
      });
    }

    wrap.innerHTML = html;
    this.state.time = null;

    $$('[data-shl-time]', wrap).forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.time = btn.getAttribute('data-shl-time');
        $$('[data-shl-time]', wrap).forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
      });
    });
  };

  ShowroomLayer.prototype.fillSummary = function () {
    var room = this.byHandle[this.state.room] || {};
    var rows = [
      ['What', this.state.mode === 'hold_designer' ? 'Hold + designer' : 'Hold for my visit'],
      ['Showroom', room.name || ''],
      ['Day', this.state.day || ''],
      ['Time', this.state.time || ''],
      ['Build', this.variantTitle()]
    ];

    var html = rows.map(function (r) {
      return '<div class="shl__summary-row"><span>' + r[0] + '</span><span>' + r[1] + '</span></div>';
    }).join('');

    var box = $('[data-shl-summary]', this.drawer);
    if (box) box.innerHTML = html;

    var doneBox = $('[data-shl-done-summary]', this.drawer);
    if (doneBox) doneBox.innerHTML = html + '<div class="shl__summary-row"><span>Address</span><span>' + (room.address || '') + '</span></div>';

    var doneText = $('[data-shl-done-text]', this.drawer);
    if (doneText) {
      doneText.textContent = this.state.mode === 'hold_designer'
        ? "A designer will have your build ready. We'll text you to confirm the time."
        : "We'll set it aside for your visit and text you to confirm.";
    }

    // Hidden fields for the posted form.
    var set = function (sel, value) { var el = $(sel, this.drawer); if (el) el.value = value; }.bind(this);
    set('[data-shl-field-showroom]', room.name || '');
    set('[data-shl-field-day]', this.state.day || '');
    set('[data-shl-field-time]', this.state.time || '');
    set('[data-shl-request-type]', this.state.mode);
    set('[data-shl-field-tags]', 'showroom-visit, ' + (this.state.room || ''));
  };

  /**
   * A calendar file built in the browser — no server round trip, and it
   * works offline. The time is the shopper's REQUESTED time, which is why
   * the summary says the store will confirm.
   */
  ShowroomLayer.prototype.downloadIcs = function () {
    var room = this.byHandle[this.state.room] || {};
    if (!this.state.day) return;

    var start = this.state.day.replace(/-/g, '') + 'T100000';
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Seldens//Showroom Visit//EN',
      'BEGIN:VEVENT',
      'DTSTART:' + start,
      'SUMMARY:Seldens visit — ' + (this.data.productTitle || ''),
      'LOCATION:' + (room.address || '').replace(/,/g, '\\,'),
      'DESCRIPTION:Requested ' + (this.state.time || '') + '. We will text you to confirm.',
      'END:VEVENT',
      'END:VCALENDAR'
    ];

    var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'seldens-visit.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    if (document.body.dataset.shlReady === 'true') return;
    document.body.dataset.shlReady = 'true';
    window.seldensShowroomLayer = new ShowroomLayer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    document.body.dataset.shlReady = '';
    init();
  });
})();
